/**
 * Email Analytics Dashboard
 * Comprehensive analytics and reporting for email campaigns
 *
 * Created: January 3, 2026
 */

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Card, Badge, ProgressBar } from "react-bootstrap";
import { toast } from "react-toastify";

import { selectUser } from "../../../components/auth/auth.selector";
import HelmetWrapper from "../../../HelmetWrapper";
import ReactVortoxSpinner from "../../../components/common-components/ReactVortoxSpinner";

import {
  fetchEmailAnalyticsData,
  fetchEmailCampaignsData,
  fetchSESQuotaData,
  fetchQueueStatsData,
} from "../../../redux/email-broadcast/emailBroadcastSlice";
import { formatDateTimezon } from "../../../components/common-data/Converts";

// Stat Card with trend
const StatCard = ({ title, value, subtitle, trend, color = "#D2000B" }) => (
  <Card className="h-100">
    <Card.Body>
      <div className="d-flex justify-content-between align-items-start">
        <div>
          <h6 className="text-muted mb-1">{title}</h6>
          <h3 className="mb-0" style={{ color }}>{value}</h3>
          {subtitle && <small className="text-muted">{subtitle}</small>}
        </div>
        {trend && (
          <Badge bg={trend > 0 ? "success" : trend < 0 ? "danger" : "secondary"}>
            {trend > 0 ? "↑" : trend < 0 ? "↓" : "→"} {Math.abs(trend)}%
          </Badge>
        )}
      </div>
    </Card.Body>
  </Card>
);

// Progress stat bar
const ProgressStat = ({ label, value, max, color = "danger", showPercent = true }) => {
  const percent = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between mb-1">
        <span>{label}</span>
        <span>
          {value.toLocaleString()}
          {showPercent && <small className="text-muted ms-1">({percent.toFixed(1)}%)</small>}
        </span>
      </div>
      <ProgressBar now={percent} variant={color} style={{ height: "8px" }} />
    </div>
  );
};

// Simple bar chart component
const SimpleBarChart = ({ data, labelKey, valueKey, color = "#D2000B" }) => {
  const maxValue = Math.max(...data.map((d) => d[valueKey] || 0));

  return (
    <div>
      {data.map((item, index) => (
        <div key={index} className="mb-3">
          <div className="d-flex justify-content-between mb-1">
            <small>{item[labelKey]}</small>
            <small className="fw-medium">{item[valueKey]?.toLocaleString() || 0}</small>
          </div>
          <div className="progress" style={{ height: "6px" }}>
            <div
              className="progress-bar"
              style={{
                width: `${maxValue > 0 ? (item[valueKey] / maxValue) * 100 : 0}%`,
                backgroundColor: color,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// Hourly heatmap component
const HourlyHeatmap = ({ data }) => {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const maxValue = Math.max(...(data || []).map((d) => d.count || 0));

  const getColor = (count) => {
    if (!count) return "#f8f9fa";
    const intensity = Math.min(count / (maxValue || 1), 1);
    const r = Math.round(210 + (0 - 210) * intensity);
    const g = Math.round(210 + (0 - 210) * intensity);
    const b = Math.round(210 + (11 - 210) * intensity);
    return `rgb(${r}, ${g}, ${b})`;
  };

  return (
    <div className="d-flex flex-wrap gap-1">
      {hours.map((hour) => {
        const hourData = data?.find((d) => d.hour === hour);
        return (
          <div
            key={hour}
            className="text-center"
            style={{
              width: "30px",
              height: "30px",
              backgroundColor: getColor(hourData?.count),
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "10px",
              color: hourData?.count > maxValue * 0.5 ? "white" : "#666",
            }}
            title={`${hour}:00 - ${hourData?.count || 0} opens`}
          >
            {hour}
          </div>
        );
      })}
    </div>
  );
};

const EmailAnalytics = () => {
  const dispatch = useDispatch();
  const userInfoData = useSelector(selectUser);
  const {
    emailAnalytics,
    emailAnalyticsLoading,
    emailCampaigns,
    sesQuota,
    sesQuotaLoading,
    queueStats,
    queueStatsLoading,
  } = useSelector((state) => state.emailBroadcast || {});

  const [dateRange, setDateRange] = useState("30d");
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    const filters = { range: dateRange };
    dispatch(fetchEmailAnalyticsData({ filters, token: userInfoData?.token }));
    dispatch(fetchEmailCampaignsData({ filters: { limit: 100 }, token: userInfoData?.token }));
    dispatch(fetchSESQuotaData({ token: userInfoData?.token }));
    dispatch(fetchQueueStatsData({ filters, token: userInfoData?.token }));
  }, [dispatch, userInfoData?.token, dateRange]);

  const analytics = emailAnalytics?.data || {};
  const campaigns = emailCampaigns?.data || [];

  // Calculate aggregate stats
  const totalSent = campaigns.reduce((acc, c) => acc + (c.sent_count || 0), 0);
  const totalOpens = campaigns.reduce((acc, c) => acc + (c.unique_open_count || 0), 0);
  const totalClicks = campaigns.reduce((acc, c) => acc + (c.unique_click_count || 0), 0);
  const totalBounces = campaigns.reduce((acc, c) => acc + (c.bounce_count || 0), 0);
  const totalUnsubscribes = campaigns.reduce((acc, c) => acc + (c.unsubscribe_count || 0), 0);

  const openRate = totalSent > 0 ? ((totalOpens / totalSent) * 100).toFixed(1) : 0;
  const clickRate = totalSent > 0 ? ((totalClicks / totalSent) * 100).toFixed(1) : 0;
  const bounceRate = totalSent > 0 ? ((totalBounces / totalSent) * 100).toFixed(2) : 0;
  const unsubRate = totalSent > 0 ? ((totalUnsubscribes / totalSent) * 100).toFixed(2) : 0;

  // Top performing campaigns
  const topCampaigns = [...campaigns]
    .filter((c) => c.sent_count > 0)
    .sort((a, b) => {
      const aRate = (a.unique_open_count || 0) / (a.sent_count || 1);
      const bRate = (b.unique_open_count || 0) / (b.sent_count || 1);
      return bRate - aRate;
    })
    .slice(0, 5);

  // Domain breakdown mock data (would come from analytics API)
  const domainBreakdown = analytics.domain_stats || [
    { domain: "gmail.com", count: Math.floor(totalSent * 0.45) },
    { domain: "yahoo.com", count: Math.floor(totalSent * 0.15) },
    { domain: "hotmail.com", count: Math.floor(totalSent * 0.12) },
    { domain: "outlook.com", count: Math.floor(totalSent * 0.08) },
    { domain: "other", count: Math.floor(totalSent * 0.20) },
  ];

  // Day of week performance mock data
  const dayPerformance = analytics.day_stats || [
    { day: "Monday", opens: 18.5 },
    { day: "Tuesday", opens: 22.1 },
    { day: "Wednesday", opens: 21.3 },
    { day: "Thursday", opens: 19.8 },
    { day: "Friday", opens: 16.2 },
    { day: "Saturday", opens: 12.4 },
    { day: "Sunday", opens: 10.8 },
  ];

  return (
    <HelmetWrapper title="WTF: Email Analytics">
      <Container className="bg-cloud-k px-50px gobal-container" fluid>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-1">Email Analytics</h4>
                <p className="text-muted mb-0">Track performance and optimize your campaigns</p>
              </div>
              <div className="d-flex gap-2">
                <select
                  className="form-select"
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  style={{ width: "150px" }}
                >
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                  <option value="1y">Last year</option>
                  <option value="all">All time</option>
                </select>
              </div>
            </div>
          </Col>
        </Row>

        {emailAnalyticsLoading ? (
          <div className="text-center py-5">
            <ReactVortoxSpinner />
          </div>
        ) : (
          <>
            {/* Overview Stats */}
            <Row className="mb-4">
              <Col md={2}>
                <StatCard
                  title="Total Sent"
                  value={totalSent.toLocaleString()}
                  subtitle={`${campaigns.length} campaigns`}
                  color="#2196F3"
                />
              </Col>
              <Col md={2}>
                <StatCard
                  title="Open Rate"
                  value={`${openRate}%`}
                  subtitle={`${totalOpens.toLocaleString()} opens`}
                  trend={analytics.open_rate_trend}
                  color="#4CAF50"
                />
              </Col>
              <Col md={2}>
                <StatCard
                  title="Click Rate"
                  value={`${clickRate}%`}
                  subtitle={`${totalClicks.toLocaleString()} clicks`}
                  trend={analytics.click_rate_trend}
                  color="#FF9800"
                />
              </Col>
              <Col md={2}>
                <StatCard
                  title="Bounce Rate"
                  value={`${bounceRate}%`}
                  subtitle={`${totalBounces.toLocaleString()} bounces`}
                  color="#f44336"
                />
              </Col>
              <Col md={2}>
                <StatCard
                  title="Unsubscribe Rate"
                  value={`${unsubRate}%`}
                  subtitle={`${totalUnsubscribes.toLocaleString()} unsubs`}
                  color="#9C27B0"
                />
              </Col>
              <Col md={2}>
                <StatCard
                  title="Delivery Rate"
                  value={`${(100 - parseFloat(bounceRate)).toFixed(1)}%`}
                  subtitle="Successful"
                  color="#4CAF50"
                />
              </Col>
            </Row>

            {/* Tabs */}
            <Row className="mb-3">
              <Col>
                <Card>
                  <Card.Body className="py-2">
                    <div className="d-flex gap-3">
                      {[
                        { key: "overview", label: "Overview" },
                        { key: "campaigns", label: "Campaigns" },
                        { key: "engagement", label: "Engagement" },
                        { key: "deliverability", label: "Deliverability" },
                      ].map((tab) => (
                        <button
                          key={tab.key}
                          className={`btn btn-sm ${activeTab === tab.key ? "btn-primary" : "btn-outline-secondary"}`}
                          onClick={() => setActiveTab(tab.key)}
                          style={activeTab === tab.key ? { backgroundColor: "#D2000B", borderColor: "#D2000B" } : {}}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Overview Tab */}
            {activeTab === "overview" && (
              <Row>
                <Col md={8}>
                  {/* Top Campaigns */}
                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">Top Performing Campaigns</h6>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead>
                            <tr>
                              <th>Campaign</th>
                              <th>Sent</th>
                              <th>Open Rate</th>
                              <th>Click Rate</th>
                              <th>CTR</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topCampaigns.map((campaign) => {
                              const cOpenRate = ((campaign.unique_open_count || 0) / (campaign.sent_count || 1) * 100).toFixed(1);
                              const cClickRate = ((campaign.unique_click_count || 0) / (campaign.sent_count || 1) * 100).toFixed(1);
                              const ctr = ((campaign.unique_click_count || 0) / (campaign.unique_open_count || 1) * 100).toFixed(1);
                              return (
                                <tr key={campaign.uid}>
                                  <td>
                                    <span className="fw-medium">{campaign.campaign_name}</span>
                                    <small className="d-block text-muted">{formatDateTimezon(campaign.date_added)}</small>
                                  </td>
                                  <td>{campaign.sent_count?.toLocaleString()}</td>
                                  <td>
                                    <Badge bg={parseFloat(cOpenRate) > 20 ? "success" : parseFloat(cOpenRate) > 10 ? "warning" : "danger"}>
                                      {cOpenRate}%
                                    </Badge>
                                  </td>
                                  <td>
                                    <Badge bg={parseFloat(cClickRate) > 3 ? "success" : parseFloat(cClickRate) > 1 ? "warning" : "secondary"}>
                                      {cClickRate}%
                                    </Badge>
                                  </td>
                                  <td>{ctr}%</td>
                                </tr>
                              );
                            })}
                            {topCampaigns.length === 0 && (
                              <tr>
                                <td colSpan="5" className="text-center text-muted py-4">
                                  No campaigns with data yet
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </Card.Body>
                  </Card>

                  {/* Day Performance */}
                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">Open Rate by Day of Week</h6>
                    </Card.Header>
                    <Card.Body>
                      {dayPerformance.map((day) => (
                        <ProgressStat
                          key={day.day}
                          label={day.day}
                          value={day.opens}
                          max={30}
                          color={day.opens > 20 ? "success" : day.opens > 15 ? "warning" : "secondary"}
                          showPercent={false}
                        />
                      ))}
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={4}>
                  {/* SES Quota */}
                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">AWS SES Status</h6>
                    </Card.Header>
                    <Card.Body>
                      {sesQuotaLoading ? (
                        <div className="text-center py-3">
                          <ReactVortoxSpinner />
                        </div>
                      ) : sesQuota ? (
                        <>
                          <div className="mb-3">
                            <div className="d-flex justify-content-between mb-1">
                              <small>Daily Usage</small>
                              <small>{sesQuota.percentUsed}%</small>
                            </div>
                            <ProgressBar
                              now={sesQuota.percentUsed}
                              variant={sesQuota.percentUsed > 80 ? "danger" : sesQuota.percentUsed > 50 ? "warning" : "success"}
                              style={{ height: "10px" }}
                            />
                          </div>
                          <Row className="text-center">
                            <Col>
                              <h5 className="mb-0">{sesQuota.sentLast24Hours?.toLocaleString()}</h5>
                              <small className="text-muted">Sent Today</small>
                            </Col>
                            <Col>
                              <h5 className="mb-0">{sesQuota.remaining?.toLocaleString()}</h5>
                              <small className="text-muted">Remaining</small>
                            </Col>
                          </Row>
                          <hr />
                          <div className="text-center">
                            <small className="text-muted">
                              Max Rate: {sesQuota.maxSendRate}/sec | Daily Limit: {sesQuota.max24HourSend?.toLocaleString()}
                            </small>
                          </div>
                        </>
                      ) : (
                        <p className="text-muted mb-0">Unable to fetch SES status</p>
                      )}
                    </Card.Body>
                  </Card>

                  {/* Domain Breakdown */}
                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">Email Domain Distribution</h6>
                    </Card.Header>
                    <Card.Body>
                      <SimpleBarChart
                        data={domainBreakdown}
                        labelKey="domain"
                        valueKey="count"
                        color="#2196F3"
                      />
                    </Card.Body>
                  </Card>

                  {/* Queue Stats */}
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Email Queue</h6>
                    </Card.Header>
                    <Card.Body>
                      {queueStatsLoading ? (
                        <div className="text-center py-3">
                          <ReactVortoxSpinner />
                        </div>
                      ) : queueStats ? (
                        <div className="row text-center">
                          <div className="col-4">
                            <h4 className="text-warning mb-0">{queueStats.pending?.toLocaleString() || 0}</h4>
                            <small className="text-muted">Pending</small>
                          </div>
                          <div className="col-4">
                            <h4 className="text-info mb-0">{queueStats.processing?.toLocaleString() || 0}</h4>
                            <small className="text-muted">Processing</small>
                          </div>
                          <div className="col-4">
                            <h4 className="text-danger mb-0">{queueStats.failed?.toLocaleString() || 0}</h4>
                            <small className="text-muted">Failed</small>
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted mb-0">No queue data available</p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Campaigns Tab */}
            {activeTab === "campaigns" && (
              <Row>
                <Col>
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">All Campaigns Performance</h6>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead>
                            <tr>
                              <th>Campaign</th>
                              <th>Status</th>
                              <th>List</th>
                              <th>Sent</th>
                              <th>Opens</th>
                              <th>Clicks</th>
                              <th>Bounces</th>
                              <th>Unsubs</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {campaigns.map((campaign) => (
                              <tr key={campaign.uid}>
                                <td className="fw-medium">{campaign.campaign_name}</td>
                                <td>
                                  <Badge
                                    bg={
                                      campaign.campaign_status === "sent" ? "success"
                                        : campaign.campaign_status === "sending" ? "warning"
                                        : campaign.campaign_status === "scheduled" ? "info"
                                        : "secondary"
                                    }
                                  >
                                    {campaign.campaign_status}
                                  </Badge>
                                </td>
                                <td>{campaign.list_name || "-"}</td>
                                <td>{campaign.sent_count?.toLocaleString() || 0}</td>
                                <td>
                                  {campaign.unique_open_count || 0}
                                  {campaign.sent_count > 0 && (
                                    <small className="text-muted ms-1">
                                      ({((campaign.unique_open_count / campaign.sent_count) * 100).toFixed(1)}%)
                                    </small>
                                  )}
                                </td>
                                <td>
                                  {campaign.unique_click_count || 0}
                                  {campaign.sent_count > 0 && (
                                    <small className="text-muted ms-1">
                                      ({((campaign.unique_click_count / campaign.sent_count) * 100).toFixed(1)}%)
                                    </small>
                                  )}
                                </td>
                                <td>{campaign.bounce_count || 0}</td>
                                <td>{campaign.unsubscribe_count || 0}</td>
                                <td>
                                  <small>{formatDateTimezon(campaign.date_added)}</small>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Engagement Tab */}
            {activeTab === "engagement" && (
              <Row>
                <Col md={6}>
                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">Opens by Hour (Best Time to Send)</h6>
                    </Card.Header>
                    <Card.Body>
                      <HourlyHeatmap data={analytics.hourly_opens} />
                      <div className="mt-3 text-center">
                        <small className="text-muted">
                          Darker colors indicate higher open rates. Best time: 10 AM - 2 PM IST
                        </small>
                      </div>
                    </Card.Body>
                  </Card>

                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Click-Through Rate (CTR) Analysis</h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="text-center mb-4">
                        <h2 style={{ color: "#D2000B" }}>
                          {totalOpens > 0 ? ((totalClicks / totalOpens) * 100).toFixed(1) : 0}%
                        </h2>
                        <p className="text-muted">Average CTR (Clicks / Opens)</p>
                      </div>
                      <div className="alert alert-info mb-0">
                        <strong>Industry Benchmark:</strong> Average email CTR is 2-5%.
                        Your CTR of {totalOpens > 0 ? ((totalClicks / totalOpens) * 100).toFixed(1) : 0}% is
                        {totalOpens > 0 && (totalClicks / totalOpens) * 100 > 3 ? " above" : " below"} average.
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">Engagement Funnel</h6>
                    </Card.Header>
                    <Card.Body>
                      <ProgressStat
                        label="Delivered"
                        value={totalSent - totalBounces}
                        max={totalSent}
                        color="success"
                      />
                      <ProgressStat
                        label="Opened"
                        value={totalOpens}
                        max={totalSent}
                        color="info"
                      />
                      <ProgressStat
                        label="Clicked"
                        value={totalClicks}
                        max={totalSent}
                        color="warning"
                      />
                      <ProgressStat
                        label="Unsubscribed"
                        value={totalUnsubscribes}
                        max={totalSent}
                        color="danger"
                      />
                    </Card.Body>
                  </Card>

                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Engagement Tips</h6>
                    </Card.Header>
                    <Card.Body>
                      <ul className="mb-0 ps-3">
                        <li className="mb-2">
                          <strong>Subject lines:</strong> Keep under 50 characters, use personalization
                        </li>
                        <li className="mb-2">
                          <strong>Send time:</strong> Test different times, typically 10 AM - 2 PM works best
                        </li>
                        <li className="mb-2">
                          <strong>Content:</strong> Use clear CTAs, keep emails concise
                        </li>
                        <li className="mb-2">
                          <strong>Mobile:</strong> 60%+ opens on mobile, always test responsiveness
                        </li>
                        <li>
                          <strong>Segmentation:</strong> Targeted emails get 14% higher open rates
                        </li>
                      </ul>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Deliverability Tab */}
            {activeTab === "deliverability" && (
              <Row>
                <Col md={6}>
                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">Deliverability Score</h6>
                    </Card.Header>
                    <Card.Body className="text-center">
                      <div
                        className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                        style={{
                          width: "120px",
                          height: "120px",
                          backgroundColor: parseFloat(bounceRate) < 2 ? "#4CAF50" : parseFloat(bounceRate) < 5 ? "#FF9800" : "#f44336",
                          color: "white",
                          fontSize: "32px",
                          fontWeight: "bold",
                        }}
                      >
                        {(100 - parseFloat(bounceRate)).toFixed(0)}%
                      </div>
                      <h5 className={parseFloat(bounceRate) < 2 ? "text-success" : parseFloat(bounceRate) < 5 ? "text-warning" : "text-danger"}>
                        {parseFloat(bounceRate) < 2 ? "Excellent" : parseFloat(bounceRate) < 5 ? "Good" : "Needs Improvement"}
                      </h5>
                      <p className="text-muted">
                        Your bounce rate is {bounceRate}%.
                        {parseFloat(bounceRate) < 2 ? " Keep up the good work!" : " Consider cleaning your list."}
                      </p>
                    </Card.Body>
                  </Card>

                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Bounce Breakdown</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row className="text-center">
                        <Col>
                          <h4 className="text-danger">{analytics.hard_bounces?.toLocaleString() || 0}</h4>
                          <small className="text-muted">Hard Bounces</small>
                          <p className="small text-muted mt-1">Invalid addresses</p>
                        </Col>
                        <Col>
                          <h4 className="text-warning">{analytics.soft_bounces?.toLocaleString() || 0}</h4>
                          <small className="text-muted">Soft Bounces</small>
                          <p className="small text-muted mt-1">Temporary issues</p>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">Spam Complaints</h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="text-center mb-3">
                        <h2 style={{ color: analytics.complaint_count > 10 ? "#f44336" : "#4CAF50" }}>
                          {analytics.complaint_count?.toLocaleString() || 0}
                        </h2>
                        <p className="text-muted">Total Complaints</p>
                      </div>
                      <div className="alert alert-warning mb-0">
                        <strong>Warning:</strong> High spam complaint rates can affect your sender reputation.
                        Keep it under 0.1% to maintain good deliverability.
                      </div>
                    </Card.Body>
                  </Card>

                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Deliverability Best Practices</h6>
                    </Card.Header>
                    <Card.Body>
                      <div className="form-check mb-2">
                        <input type="checkbox" className="form-check-input" checked readOnly />
                        <label className="form-check-label">SPF record configured</label>
                      </div>
                      <div className="form-check mb-2">
                        <input type="checkbox" className="form-check-input" checked readOnly />
                        <label className="form-check-label">DKIM signing enabled</label>
                      </div>
                      <div className="form-check mb-2">
                        <input type="checkbox" className="form-check-input" checked readOnly />
                        <label className="form-check-label">DMARC policy set</label>
                      </div>
                      <div className="form-check mb-2">
                        <input type="checkbox" className="form-check-input" checked readOnly />
                        <label className="form-check-label">Unsubscribe link in all emails</label>
                      </div>
                      <div className="form-check mb-2">
                        <input type="checkbox" className="form-check-input" checked readOnly />
                        <label className="form-check-label">AWS SES verified domain</label>
                      </div>
                      <div className="form-check">
                        <input type="checkbox" className="form-check-input" checked readOnly />
                        <label className="form-check-label">Bounce/complaint handling active</label>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </>
        )}
      </Container>
    </HelmetWrapper>
  );
};

export default EmailAnalytics;
